<template>
  <el-dialog
    title="领用记录详情"
    :visible="visible"
    width="740px"
    append-to-body
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <div v-loading="loading" class="dialog-body">
      <template v-if="!loading && detail">
        <!-- 基础信息：领用单维度 -->
        <div class="section-block">
          <div class="section-title">领用信息</div>
          <div class="custom-desc">
            <div class="desc-row">
              <div class="desc-item">
                <div class="desc-label">领用单号</div>
                <div class="desc-value">
                  <code v-if="detail.materialOutCode" class="highlight-code">{{ detail.materialOutCode }}</code>
                  <span v-else>-</span>
                </div>
              </div>
              <div class="desc-item">
                <div class="desc-label">申请状态</div>
                <div class="desc-value">
                  <el-tag :type="outStatusType(detail.status)" size="small" effect="light">
                    {{ outStatusLabel(detail.status) }}
                  </el-tag>
                </div>
              </div>
            </div>
            <div class="desc-row">
              <div class="desc-item">
                <div class="desc-label">申请人</div>
                <div class="desc-value">{{ detail.applyUserName || '-' }}</div>
              </div>
              <div class="desc-item">
                <div class="desc-label">申请时间</div>
                <div class="desc-value">{{ formatDateTime(detail.applyTime) }}</div>
              </div>
            </div>
            <div class="desc-row">
              <div class="desc-item">
                <div class="desc-label">领用材料</div>
                <div class="desc-value">{{ detail.materialName || '-' }}</div>
              </div>
              <div class="desc-item">
                <div class="desc-label">领用数量</div>
                <div class="desc-value">
                  {{ detail.outCount != null ? `${detail.outCount}${detail.unit || ''}` : '-' }}
                </div>
              </div>
            </div>
            <div class="desc-row">
              <div class="desc-item">
                <div class="desc-label">材料编号</div>
                <div class="desc-value">
                  <code class="highlight-code">{{ detail.materialCode || '-' }}</code>
                </div>
              </div>
              <div class="desc-item">
                <div class="desc-label">材料类型</div>
                <div class="desc-value">
                  <dict-tag :dict-type="DICT_TYPE.MATERIAL_TYPE" :value="detail.materialType" />
                </div>
              </div>
            </div>
            <div class="desc-row">
              <div class="desc-item" style="flex: 1">
                <div class="desc-label">用途说明</div>
                <div class="desc-value desc-value--multiline">{{ detail.purpose || '-' }}</div>
              </div>
            </div>
            <div v-if="!hasFaultLink && detail.zoneAreaName" class="desc-row">
              <div class="desc-item" style="flex: 1">
                <div class="desc-label">使用区域</div>
                <div class="desc-value">{{ detail.zoneAreaName }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 故障信息（仅关联故障单时展示） -->
        <div v-if="hasFaultLink" class="section-block">
          <div class="section-title">关联故障信息</div>
          <div class="custom-desc">
            <div class="desc-row">
              <div class="desc-item" style="flex: 1">
                <div class="desc-label">关联故障单</div>
                <div class="desc-value">
                  <el-link
                    v-if="detail.faultCode"
                    type="primary"
                    :underline="false"
                    @click="goFaultListFromDetail"
                  >{{ detail.faultCode }}</el-link>
                  <span v-else>-</span>
                </div>
              </div>
            </div>
            <div v-if="faultDeviceSummary" class="desc-row">
              <div class="desc-item" style="flex: 1">
                <div class="desc-label">故障设备</div>
                <div class="desc-value desc-value--multiline">{{ faultDeviceSummary }}</div>
              </div>
            </div>
            <div v-if="detail.areaPath" class="desc-row">
              <div class="desc-item" style="flex: 1">
                <div class="desc-label">设备所在区域</div>
                <div class="desc-value desc-value--multiline">{{ detail.areaPath }}</div>
              </div>
            </div>
            <div v-if="detail.installLocation" class="desc-row">
              <div class="desc-item" style="flex: 1">
                <div class="desc-label">具体位置</div>
                <div class="desc-value desc-value--multiline">{{ detail.installLocation }}</div>
              </div>
            </div>
            <div v-if="detail.faultDesc" class="desc-row">
              <div class="desc-item" style="flex: 1">
                <div class="desc-label">故障描述</div>
                <div class="desc-value desc-value--multiline">{{ detail.faultDesc }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 审批信息（仅已通过 / 已拒绝后展示） -->
        <div v-if="showApprovalSection" class="section-block">
          <div class="section-title">审批信息</div>
          <div class="custom-desc">
            <div class="desc-row">
              <div class="desc-item">
                <div class="desc-label">审批人</div>
                <div class="desc-value">{{ detail.approveUserName || '-' }}</div>
              </div>
              <div class="desc-item">
                <div class="desc-label">审批时间</div>
                <div class="desc-value">{{ formatDateTime(detail.approveTime) }}</div>
              </div>
            </div>
            <div class="desc-row">
              <div class="desc-item" style="flex: 1">
                <div class="desc-label">审批意见</div>
                <div class="desc-value desc-value--multiline">{{ detail.remark || '-' }}</div>
              </div>
            </div>
          </div>
        </div>
      </template>
      <div v-else-if="!loading && loadError" class="load-error">{{ loadError }}</div>
    </div>

    <div slot="footer">
      <el-button @click="handleClose">关闭</el-button>
    </div>
  </el-dialog>
</template>

<script>
// 服务
import { materialService } from './material.service';
// 工具函数
import { DICT_TYPE } from '@/features/components/dict/dictTypeKeys';
import { formatDateTime } from '@/features/common/utils/dateTimeUtil.js';

export default {
  name: 'MaterialOutDetailDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    /** 领用记录主键，打开弹窗时由列表传入 */
    uuid: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      DICT_TYPE,
      detail: null,
      loading: false,
      loadError: ''
    };
  },
  computed: {
    hasFaultLink() {
      const id = this.detail && this.detail.faultId;
      return id != null && String(id).trim() !== '';
    },
    faultDeviceSummary() {
      const d = this.detail || {};
      const name = d.deviceName == null ? '' : String(d.deviceName).trim();
      const code = d.deviceCode == null ? '' : String(d.deviceCode).trim();
      if (name && code) {
        return `${name}(${code})`;
      }
      return name || code || '';
    },
    /** 待审批不展示审批区块；结束后展示 */
    showApprovalSection() {
      const s = (this.detail || {}).status;
      return s === 'approved' || s === 'rejected';
    }
  },
  watch: {
    /** 首次挂载时 visible 已是 true，须 immediate 才会拉取；关闭时清空 */
    visible: {
      immediate: true,
      handler(val) {
        if (val && this.uuid) {
          this.fetchDetail();
        } else if (!val) {
          this.detail = null;
          this.loadError = '';
        }
      }
    },
    uuid() {
      if (this.visible && this.uuid) {
        this.fetchDetail();
      }
    }
  },
  methods: {
    formatDateTime,
    handleClose() {
      this.$emit('update:visible', false);
    },
    fetchDetail() {
      this.loading = true;
      this.loadError = '';
      this.detail = null;
      materialService.getMaterialOutDetail(this.uuid).then((res) => {
        const data = res.data;
        if (!data || !data.uuid) {
          this.loadError = '未找到领用记录';
          return;
        }
        this.detail = data;
      }).finally(() => {
        this.loading = false;
      });
    },
    outStatusType(status) {
      const map = { pending: 'warning', approved: 'success', rejected: 'danger' };
      return map[status] || 'info';
    },
    outStatusLabel(status) {
      const map = { pending: '待审批', approved: '已通过', rejected: '已拒绝' };
      return map[status] || status;
    },
    goFaultListFromDetail() {
      const code = this.detail && this.detail.faultCode ? String(this.detail.faultCode).trim() : '';
      if (!code) return;
      this.handleClose();
      this.$nextTick(() => {
        this.$router.push({ name: 'faultList', query: { keyword: code, listTab: 'all' } }).catch(() => {});
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.dialog-body {
  min-height: 120px;
}

.section-block {
  margin-bottom: 16px;
}
.section-block:last-child {
  margin-bottom: 0;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 10px;
  padding-left: 10px;
  border-left: 4px solid #409eff;
  line-height: 1.1;
}

.custom-desc {
  border: 1px solid #ebeef5;
  border-radius: 6px;
  overflow: hidden;
  font-size: 14px;
  background: #fff;
}

.desc-row {
  display: flex;
  border-bottom: 1px solid #ebeef5;

  &:last-child {
    border-bottom: none;
  }
}

.desc-item {
  display: flex;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  border-right: 1px solid #ebeef5;

  &:last-child {
    border-right: none;
  }
}

.desc-label {
  width: 110px;
  background-color: #f8f9fb;
  color: #606266;
  padding: 10px 12px;
  box-sizing: border-box;
  font-weight: 500;
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.desc-value {
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
  color: #303133;
  box-sizing: border-box;
  word-break: break-all;
  display: flex;
  align-items: center;
  overflow: hidden;
}

.desc-value--multiline {
  white-space: pre-wrap;
  line-height: 1.6;
  align-items: flex-start;
  padding-top: 5px;
  padding-bottom: 5px;
}

.highlight-code {
  color: #409eff;
  background: #ecf5ff;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: monospace;
}

.load-error {
  text-align: center;
  color: #f56c6c;
  padding: 24px 0;
  font-size: 14px;
}
</style>
